(ns nosco.ui.layouts
  (:require
   ["react" :as React]
   ["react-is" :as ReactIs]
   [hx.hooks :as hooks]
   [hx.react :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.icons2 :as icons2]
   [nosco.react]
   [nosco.shared.layouts :as shared-layouts]
   [nosco.ui.buttons2 :as buttons]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text :refer [classnames]]))

(defonce BackgroundColorContext (React/createContext (fn [variant])))

;; allow child component to influence the body element color
;; this is the most robust technique I could find, it doesn't introduce
;; any new wrappers potentially messing with layout, and also works
;; nicely on mobile when you drag and get the rubber-band action,
;; as that's a case where you peek the body element underneath
(defn set-background-variant [background-variant]
  (set! (.-backgroundColor (.-style (.-body js/document)))
        (case background-variant
          :gray colors/neutral-20
          :white colors/white
          :default "")))

(defnc PageWithHeader [{:keys [header support-btn children]}]
  (shared-layouts/page-with-header
   {:header header
    :support-btn support-btn
    :children children}))

(defn use-set-body-background-color
  "Set the body background color to either :gray or :white."
  [variant]
  (hooks/useLayoutEffect (fn []
                           (set-background-variant variant)
                           #(set-background-variant :default))
                         [variant]))

(defnc IdeaCreationPage [{:keys [children]}]
  [:section {:class "nosco-layout-idea-creation-page"}
   children])

(defnc Standalone
  "A layout that is full width with no sidebar support."
  [{:keys [children]}]
  [:section {:class "nosco-layout-standalone-page"}
   children])

(defnc ^:private DefaultPageSidebarLeft [{:keys [children]} ref]
  [:div {:class "nosco-layout-default-page__sidebar-left"
         :ref ref}
   children])

(nosco.react/forward-ref! DefaultPageSidebarLeft)

(defnc ^:private FixedSidebar [{:keys [children class on-collapse]} ref]
  (let [ref (or ref (React/useRef))]
    [:div {:class (nosco.react/merge-class "nosco-layout-fixed-nav__sidebar-left" class)
           :ref ref}
     ;; The OverlayContainerContext is used to ensure that the collapsible sidebar does not disappear
     ;; when the cursor enters an overlay (submit post dropdown, ...)
     [:provider {:context popup/OverlayContainerContext
                 :value (.-current ref)}
      children
      (when on-collapse
        ;; We make the collapse button to be shown when the wrapper is hovered.
        ;; The wrapper has a width of a few pixels and is positioned to the right
        ;; of the sidebar, so the collapse button is shown when hovering slightly
        ;; right of the sidebar. This gives a better user experience.
        [:div {:class "nosco-layout-fixed-nav__collapse-button-wrapper"}
         [:div {:class "nosco-layout-fixed-nav__collapse-button"}
          [buttons/Button {:variant :icon-light
                           :size :small
                           :icon icons2/ChevronSmall-small
                           :on-click on-collapse}]]])]]))

(nosco.react/forward-ref! FixedSidebar)

(defn- sidebar-collapse-on-mouse-enter [sidebar-section-el set-collapsed-hover set-collapse-animation-end e]
  (let [classes (when sidebar-section-el (.-classList sidebar-section-el))]
    (when (and classes
               (not (.contains classes "nosco-layout-fixed-nav--collapsed-hover"))
               (.contains classes "nosco-layout-fixed-nav--collapse-animation-end"))
      (set-collapse-animation-end false)
      (set-collapsed-hover true))))

(defn- sidebar-expand-on-mouse-leave [sidebar-section-el set-collapsed-hover set-collapse-animation-end e]
  (let [classes (when sidebar-section-el (.-classList sidebar-section-el))]
    (when (and classes
               (.contains classes "nosco-layout-fixed-nav--collapsed-hover")
               (.contains classes "nosco-layout-fixed-nav--collapse-animation-end"))
      (set-collapse-animation-end false)
      (set-collapsed-hover false))))

(defn- on-collapse-transition-end [set-collapse-animation-end e]
  (let [el (.-target e)
        classes (when el (.-classList el))]
    (when (and classes
               (.contains classes "nosco-layout-fixed-nav__sidebar-left"))
      (set-collapse-animation-end true))))

(defnc WithFixedSidebar
  "A layout that is full width, with a fixed left sidebar"
  [{:keys [children sidebar collapsible? hide-mobile-nav]}]
  (let [[collapsed set-collapsed] (hooks/useState false)
        [collapse-animation-end set-collapse-animation-end] (hooks/useState false)
        [collapsed-hover set-collapsed-hover] (hooks/useState false)
        ref (React/useRef)]
    (hooks/useEffect
     (fn []
       (when-let [el (.-current ref)]
         (when-let [sidebar-left-el (.querySelector el ".nosco-layout-fixed-nav__sidebar-left")]
           (when-let [sidebar-switcher-el (.querySelector el ".nosco-user-sidebar-switcher")]
             (.addEventListener sidebar-switcher-el "mouseenter"
                                (partial sidebar-collapse-on-mouse-enter el set-collapsed-hover set-collapse-animation-end))
             (.addEventListener sidebar-left-el "mouseleave"
                                (partial sidebar-expand-on-mouse-leave el set-collapsed-hover set-collapse-animation-end))))))
     [])
    [:section {:ref ref
               :class ["nosco-layout-fixed-nav"
                       (when collapsed "nosco-layout-fixed-nav--collapsed")
                       (when collapse-animation-end "nosco-layout-fixed-nav--collapse-animation-end")
                       (when collapsed-hover "nosco-layout-fixed-nav--collapsed-hover")
                       (when hide-mobile-nav "nosco-layout-fixed-nav--hide-mobile-nav")]
               :onTransitionEnd (partial on-collapse-transition-end set-collapse-animation-end)}
     children
     [FixedSidebar {:on-collapse (when collapsible?
                                   #(do
                                      (set-collapsed (not collapsed))
                                      (set-collapse-animation-end false)
                                      (set-collapsed-hover false)))}
      sidebar]]))

(defnc EdgeToEdge [{:keys [children] :as props}]
  [:div (nosco.react/merge-props
         {:class "nosco-layout-fixed-nav--full"}
         props)
   children])

(defnc Inset [{:keys [children] :as props}]
  [:div (nosco.react/merge-props
         {:class "nosco-layout-fixed-nav--inset"}
         props)
   children])

(defnc SidebarDrawerPortal
  "Use this add a sliding drawer on the left"
  [{:keys [open children]}]
  [:div {:class ["nosco-layout-left-drawer"
                 (when open
                   "nosco-layout-left-drawer--open")]}
   children])

(defnc DefaultPageError [{:keys [children]}]
  [:div {:class "nosco-layout-default-page__page-error"}
   [:div
    children]])

(defnc PageFloatingBar [{:keys [left right mobile-left mobile-right]}]
  [:div {:class "nosco-layout-page-floating-bar"}
   [:div {:class "nosco-layout-page-floating-bar__left"}
    left]
   [:div {:class "nosco-layout-page-floating-bar__right"}
    right]
   [:div {:class "nosco-layout-page-floating-bar__mobile-left"}
    mobile-left]
   [:div {:class "nosco-layout-page-floating-bar__mobile-right"}
    mobile-right]])

(defnc PageFloatingBarSpacer []
  [:div {:class "nosco-layout-page-floating-bar-spacer"}])

(defnc PageInfoBar [{:keys [children]}]
  [:div {:class "nosco-layout-page-info-bar"}
   [:div {:class "nosco-layout-page-info-bar__internal"}
    children]])

(defnc LegacySidebar [{:keys [children]}]
  [:aside {:class "nosco-layout-sidebar"}
   children])

(defnc Page [{:keys [title children]}]
  [:div {:class "nosco-layout-page"}
   (when title
     [:h1 {:class ["nosco-layout-page__title"]} title])
   [:div {:class "nosco-layout-page__sections"}
    children]])

(defnc PageTitle [{:keys [title]}]
  [:h1 {:class ["nosco-layout-page__title"]} title])

(defnc PageHeaderCards [{:keys [main side]}]
  [:div {:class "nosco-layout-page-header-cards"}
   [:div {:class "nosco-layout-page-header-cards__main"}
    main]
   [:div {:class "nosco-layout-page-header-cards__side"}
    side]])

(defnc PageSection [{:keys [title icon header children]}]
  [:section {:class "nosco-layout-pagesection"}
   (when title
     [:div {:class "nosco-layout-pagesection__title-wrap"}
      [text/Text {:el :h1
                  :variant text/Heading-Sm
                  :class ["nosco-layout-pagesection__title" "text-neutral-900"]}
       title]
      (when icon
        [icons2/IconOnGrayCircle {:icon icon}])])
   (when header
     [:header {:class "nosco-layout-pagesection__header"}
      header])
   children])

(defnc ChannelsGrid [{:keys [children layout featured?]}]
  (let [unwrapped-children (if (ReactIs/isFragment children)
                             (.. children -props -children)
                             children)
        total (->> (.toArray React/Children unwrapped-children)
                   (filter React/isValidElement)
                   count)
        variant (cond
                  (and featured? (= total 1)) :widest
                  (and featured? (= total 2)) :wide
                  (and featured? (= total 3)) :narrow
                  featured? :wide
                  (= :list layout) :list
                  :else :default)]
    [:div {:class (classnames "nosco-layout-channels-grid"
                              {"--widest" (= variant :widest)
                               "--wide" (= variant :wide)
                               "--narrow" (= variant :narrow)
                               "--default" (= variant :default)
                               "--list" (= variant :list)})}
     (.map React/Children
           unwrapped-children
           #(when (React/isValidElement %)
              [:div {:class "nosco-layout-channels-grid__item"}
               (React/cloneElement % #js {:featured? featured?
                                          :variant variant} nil)]))]))

(defnc AdminLayout [{:keys [header sidebar page]}]
  [:div {:class ["nosco-layout-admin"]}
   [:div {:class ["nosco-layout-admin__header"]}
    header]
   [:div {:class ["nosco-layout-admin__body"
                  (when-not sidebar
                    "nosco-layout-admin__body--full-width")]}
    (when sidebar
      [:div {:class ["nosco-layout-admin__sidebar"]}
       sidebar])
    [:div {:class ["nosco-layout-admin__page"]}
     page]]])

(defnc Column
  "A flex-based column that obeys the parent's max-width, and ensures the
  children take up the full width without overflowing or stretching.

   Use helper classes such as align-items-center, justify-content-center to adjust alignment.
Gap is passed through to :gap style property, prefer to use pixels here.
   "
  [{:keys [gap children el]
    :or {el :div}
    :as props} ref]
  {:wrap [(React/forwardRef)]}
  [el (nosco.react/merge-props
       {:class ["nosco-column"]
        :ref ref
        :style {:--nosco-column-gap gap}}
       (dissoc props :gap))
   children])

(defnc Row
  "A flex-based row. Use helper classes such as align-items-center, justify-content-center to adjust alignment.
   Gap is passed through to :gap style property, prefer to use pixels here."
  [{:keys [gap children el]
    :or {el :div}
    :as props} ref]
  {:wrap [(React/forwardRef)]}
  [el (nosco.react/merge-props
       {:class "nosco-row"
        :ref ref
        :style {:--nosco-row-gap gap}}
       (dissoc props :gap))
   children])

(defnc Grid [{:keys [el children gap grid] :as props
              :or {el :div}} ref]
  {:wrap [(React/forwardRef)]}
  [el (nosco.react/merge-props
       {:ref ref
        :style {:gap gap
                :display "grid"
                :grid grid}}
       (dissoc props :grid :gap))
   children])
